/**
 * @fileoverview
 * This component contains the form for updating the charger information in the SetInfo page of the admin page.
 *
 *
 * Parent element: AdminSetInfo.js.js
 */

import React, { useState, useEffect, useRef } from "react";
import useProForma from "store/useProForma";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Input } from "components//ui/input";
import useAuthStore from "store/useAuthStore";
import { useToast } from "components/hooks/use-toast";
import { Separator } from "components/ui/separator";
import { NumericFormat } from "react-number-format";
import {
  updateDefaultChargerData,
  updateChargerData,
  fetchDefaultChargerData,
  fetchChargerData,
} from "api/chargercostapi";
import { updateCityInfo } from "api/cityInfo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import ControlLabel from "components/advanced-controls/ControlLabel";

const ChargerInfoForm = ({ onDirtyChange }) => {
  const { cityInfo, fetchAndUpdateFleet } = useProForma();
  const [cityFormData, setCityFormData] = useState({
    range_1: "",
    range_2: "",
    range_3: "",
    range_4: "",
    port_count_1: null,
    port_count_2: null,
    port_count_3: null,
    port_count_4: null,
  });
  const [formData, setFormData] = useState({
    cost_less_than_10_kw: null,
    cost_10_20_kw: null,
    cost_25_kw: null,
    cost_180_200_kw: null,
    install_less_than_10_kw: null,
    install_10_20_kw: null,
    install_25_kw: null,
    install_180_200_kw: null,
    contingency: null,
  });

  const { toast } = useToast();
  const { user } = useAuthStore();
  const initialChargerRef = useRef(formData);
  const initialCityRef = useRef(cityFormData);
  const lastDirtyRef = useRef(false);

  // Updates the form data - Admin page fetches from "default data" table, falls back to "charger costs" table
  useEffect(() => {
    const loadDefaultChargerData = async () => {
      if (!cityInfo || !user) return;

      let chargerDataToUse = null;

      // First, try to fetch from "default data" table's charger_costs column
      const defaultChargerDataResponse = await fetchDefaultChargerData(user.id);

      if (defaultChargerDataResponse.data) {
        // Use data from "default data" table
        chargerDataToUse = defaultChargerDataResponse.data;
      } else {
        // Fall back to "charger costs" table if default data is empty
        const chargerCostsResponse = await fetchChargerData(user.id);
        if (chargerCostsResponse.data) {
          chargerDataToUse = chargerCostsResponse.data;
        }
      }

      if (chargerDataToUse) {
        setFormData({
          cost_less_than_10_kw: chargerDataToUse.cost_less_than_10_kw ?? null,
          cost_10_20_kw: chargerDataToUse.cost_10_20_kw ?? null,
          cost_25_kw: chargerDataToUse.cost_25_kw ?? null,
          cost_180_200_kw: chargerDataToUse.cost_180_200_kw ?? null,
          install_less_than_10_kw:
            chargerDataToUse.install_less_than_10_kw ?? null,
          install_10_20_kw: chargerDataToUse.install_10_20_kw ?? null,
          install_25_kw: chargerDataToUse.install_25_kw ?? null,
          install_180_200_kw: chargerDataToUse.install_180_200_kw ?? null,
          contingency: chargerDataToUse.contingency ?? null,
        });

        const nextCharger = {
          cost_less_than_10_kw: chargerDataToUse.cost_less_than_10_kw ?? null,
          cost_10_20_kw: chargerDataToUse.cost_10_20_kw ?? null,
          cost_25_kw: chargerDataToUse.cost_25_kw ?? null,
          cost_180_200_kw: chargerDataToUse.cost_180_200_kw ?? null,
          install_less_than_10_kw:
            chargerDataToUse.install_less_than_10_kw ?? null,
          install_10_20_kw: chargerDataToUse.install_10_20_kw ?? null,
          install_25_kw: chargerDataToUse.install_25_kw ?? null,
          install_180_200_kw: chargerDataToUse.install_180_200_kw ?? null,
          contingency: chargerDataToUse.contingency ?? null,
        };
        initialChargerRef.current = nextCharger;
        lastDirtyRef.current = false;
      }

      // Set city form data
      setCityFormData({
        range_1: cityInfo.range_1 ?? "",
        range_2: cityInfo.range_2 ?? "",
        range_3: cityInfo.range_3 ?? "",
        range_4: cityInfo.range_4 ?? "",
        port_count_1: cityInfo.port_count_1 ?? null,
        port_count_2: cityInfo.port_count_2 ?? null,
        port_count_3: cityInfo.port_count_3 ?? null,
        port_count_4: cityInfo.port_count_4 ?? null,
      });

      const nextCity = {
        range_1: cityInfo.range_1 ?? "",
        range_2: cityInfo.range_2 ?? "",
        range_3: cityInfo.range_3 ?? "",
        range_4: cityInfo.range_4 ?? "",
        port_count_1: cityInfo.port_count_1 ?? null,
        port_count_2: cityInfo.port_count_2 ?? null,
        port_count_3: cityInfo.port_count_3 ?? null,
        port_count_4: cityInfo.port_count_4 ?? null,
      };
      initialCityRef.current = nextCity;
      if (onDirtyChange) onDirtyChange(false);
    };

    loadDefaultChargerData();
  }, [cityInfo, user, onDirtyChange]);

  useEffect(() => {
    const chargerDirty =
      JSON.stringify(formData) !== JSON.stringify(initialChargerRef.current);
    const cityDirty =
      JSON.stringify(cityFormData) !== JSON.stringify(initialCityRef.current);
    const isDirty = chargerDirty || cityDirty;
    if (isDirty !== lastDirtyRef.current) {
      if (onDirtyChange) onDirtyChange(isDirty);
      lastDirtyRef.current = isDirty;
    }
  }, [formData, cityFormData, onDirtyChange]);

  // Handles numeric input changes from NumericFormat
  const handleNumericFieldChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value === "" || value === undefined ? null : Number(value),
    }));
  };

  const handleCityInputChange = (e) => {
    const { name, value } = e.target;
    setCityFormData((prevData) => ({
      ...prevData,
      [name]: value === "" ? null : value,
    }));
  };

  const handleSelectChange = (value, field) => {
    setCityFormData((prevData) => ({
      ...prevData,
      [field]: parseInt(value),
    }));
  };
  // Submits the form data to the backend, calls fetchAndUpdateFleet to update data
  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const userId = cityInfo.id; // Assuming userId is available in cityInfo

      // Update "default data" table's charger_costs column
      const response = await updateDefaultChargerData(userId, formData);

      // Also update "charger costs" table to override/sync the data
      const chargerCostsResponse = await updateChargerData(userId, formData);

      const cityResponse = await updateCityInfo(userId, cityFormData);

      if (response.error) {
        throw new Error(`Failed to update data: ${response.error}`);
      }

      if (chargerCostsResponse.error) {
        throw new Error(`Failed to update data: ${chargerCostsResponse.error}`);
      }

      if (cityResponse.error) {
        throw new Error(`Failed to update data: ${cityResponse.error}`);
      }

      toast({
        title: "Success",
        description: `Charger Data has been saved`,
      });
      fetchAndUpdateFleet();
      // Reset dirty baseline after successful save
      initialChargerRef.current = formData;
      initialCityRef.current = cityFormData;
      if (onDirtyChange) onDirtyChange(false);
      lastDirtyRef.current = false;
    } catch (error) {
      console.error("Error updating data:", error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-6">
        {/* Cost Settings */}
        <div className="space-y-4">
          <ControlLabel
            text={
              <span className="text-card-foreground text-lg font-medium">
                Contingency
              </span>
            }
            info=""
          />

          <div className="flex items-center justify-between w-full">
            <div className="w-1/2">
              <ControlLabel
                text="Contingency"
                info=""
              />
            </div>
            <NumericFormat
              customInput={Input}
              name="contingency"
              value={formData.contingency || ""}
              onValueChange={(values) =>
                handleNumericFieldChange("contingency", values.value)
              }
              suffix="%"
              thousandSeparator={true}
              decimalScale={0}
              allowNegative={false}
              placeholder="%"
            />
          </div>
        </div>
        <Separator />
        <div className="space-y-4">
          <ControlLabel
            text={
              <span className="text-card-foreground text-lg font-medium">
                Charger Equipment Costs Per Station
              </span>
            }
            info="The upfront price of charging equipment, including the charger unit, mounting hardware, and cables/connectors."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{`Cost ${cityInfo?.range_1 || "< 10 kW"}`}</Label>
              <NumericFormat
                customInput={Input}
                name="cost_less_than_10_kw"
                value={formData.cost_less_than_10_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("cost_less_than_10_kw", values.value)
                }
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="space-y-2">
              <Label>{`Cost ${cityInfo?.range_2 || "10-20 kW"}`}</Label>
              <NumericFormat
                customInput={Input}
                name="cost_10_20_kw"
                value={formData.cost_10_20_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("cost_10_20_kw", values.value)
                }
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="space-y-2">
              <Label>{`Cost ${cityInfo?.range_3 || "40 kW"}`}</Label>
              <NumericFormat
                customInput={Input}
                name="cost_25_kw"
                value={formData.cost_25_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("cost_25_kw", values.value)
                }
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="space-y-2">
              <Label>{`Cost ${cityInfo?.range_4 || "180-200 kW"}`}</Label>
              <NumericFormat
                customInput={Input}
                name="cost_180_200_kw"
                value={formData.cost_180_200_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("cost_180_200_kw", values.value)
                }
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Installation Costs */}
        <div className="space-y-4">
          <ControlLabel
            text={
              <span className="text-card-foreground text-lg font-medium">
                Charger Installation Costs Per Station
              </span>
            }
            info="The cost to place the charger into service at a site, including electrical work, trenching, conduit installation, panel or transformer upgrades, and associated labor."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{`Install Cost ${cityInfo?.range_1 || "< 10 kW"}`}</Label>
              <NumericFormat
                customInput={Input}
                name="install_less_than_10_kw"
                value={formData.install_less_than_10_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange(
                    "install_less_than_10_kw",
                    values.value
                  )
                }
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="space-y-2">
              <Label>{`Install Cost ${cityInfo?.range_2 || "10-20 kW"}`}</Label>
              <NumericFormat
                customInput={Input}
                name="install_10_20_kw"
                value={formData.install_10_20_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("install_10_20_kw", values.value)
                }
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="space-y-2">
              <Label>{`Install Cost ${cityInfo?.range_3 || "40 kW"}`}</Label>
              <NumericFormat
                customInput={Input}
                name="install_25_kw"
                value={formData.install_25_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("install_25_kw", values.value)
                }
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="space-y-2">
              <Label>{`Install Cost ${
                cityInfo?.range_4 || "180-200 kW"
              }`}</Label>
              <NumericFormat
                customInput={Input}
                name="install_180_200_kw"
                value={formData.install_180_200_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("install_180_200_kw", values.value)
                }
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
          </div>
        </div>

        {/* Range Settings */}
        <div className="space-y-4">
          <ControlLabel
            text={
              <span className="text-card-foreground text-lg font-medium">
                Range Settings
              </span>
            }
            info="Set the charger power ratings or ranges for four charger options (e.g., <10 kW; 10-20 kW; 30-60 kW; 100 kW+)."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex gap-4">
              <div className="space-y-2">
                <Label>Range 1:</Label>
                <Input
                  type="text"
                  name="range_1"
                  value={cityFormData.range_1}
                  onChange={handleCityInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label>Select Single/Dual/Triple for Station Type 1</Label>
                <Select
                  onValueChange={(value) =>
                    handleSelectChange(value, "port_count_1")
                  }
                  value={cityFormData.port_count_1}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a port count" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={1}>Single</SelectItem>
                    <SelectItem value={2}>Dual</SelectItem>
                    <SelectItem value={3}>Triple</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="space-y-2">
                <Label>Range 2:</Label>
                <Input
                  type="text"
                  name="range_2"
                  value={cityFormData.range_2}
                  onChange={handleCityInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label>Select Single/Dual/Triple for Station Type 2</Label>

                <Select
                  onValueChange={(value) =>
                    handleSelectChange(value, "port_count_2")
                  }
                  value={cityFormData.port_count_2}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a port count" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={1}>Single</SelectItem>
                    <SelectItem value={2}>Dual</SelectItem>
                    <SelectItem value={3}>Triple</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="space-y-2">
                <Label>Range 3:</Label>
                <Input
                  type="text"
                  name="range_3"
                  value={cityFormData.range_3}
                  onChange={handleCityInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label>Select Single/Dual/Triple for Station Type 3</Label>

                <Select
                  onValueChange={(value) =>
                    handleSelectChange(value, "port_count_3")
                  }
                  value={cityFormData.port_count_3}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a port count" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={1}>Single</SelectItem>
                    <SelectItem value={2}>Dual</SelectItem>
                    <SelectItem value={3}>Triple</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="space-y-2">
              <div className="space-y-2">
                <Label>Range 4:</Label>
                <Input
                  type="text"
                  name="range_4"
                  value={cityFormData.range_4}
                  onChange={handleCityInputChange}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Select Single/Dual/Triple for Station Type 4</Label>

              <Select
                onValueChange={(value) =>
                  handleSelectChange(value, "port_count_4")
                }
                value={cityFormData.port_count_4}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a port count" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={1}>Single</SelectItem>
                  <SelectItem value={2}>Dual</SelectItem>
                  <SelectItem value={3}>Triple</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit">Update</Button>
        </div>
      </div>
    </form>
  );
};

export default ChargerInfoForm;
